import { CommonModule, DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  inject,
  OnInit,
  PLATFORM_ID,
  REQUEST,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Meta, Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { ChipModule } from "primeng/chip";
import { RatingModule } from "primeng/rating";
import { StepsModule } from "primeng/steps";
import {
  catchError,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from "rxjs";
import { landingServices } from "../../landing.service";
import { ApiJob, buildJobPostingSchema } from "./job-schema.mapper";
import { SchemaJsonLdComponent } from "./schema-jsonld.component";

/** Route params relevant to this view (SSR + client). */
interface InviteRouteParams {
  uuid: string | null;
  domain: string | null;
}

@Component({
  selector: "app-uuid-invite-card",
  standalone: true,
  imports: [
    ChipModule,
    StepsModule,
    CommonModule,
    RatingModule,
    FormsModule,
    SchemaJsonLdComponent,
  ],
  templateUrl: "./uuid-invite-card.component.html",
  styleUrls: ["./uuid-invite-card.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UuidInviteCardComponent implements OnInit {
  private readonly document = inject(DOCUMENT);
  private readonly request = inject(REQUEST, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  schema: Record<string, unknown> | null = null;
  data: ApiJob | null = null;
  uuid: string | null = null;
  domainName: string | null = null;
  partnerInstituteDomainName: any;
  sourceName: string | null = null;
  ifExist = false;
  dominantColor = "#ffffff";
  isUniprep = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly landingService: landingServices,
    private readonly title: Title,
    private readonly meta: Meta,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map(
          (pm): InviteRouteParams => ({
            uuid: pm.get("uuid"),
            domain: pm.get("domain"),
          }),
        ),
        distinctUntilChanged(
          (a, b) => a.uuid === b.uuid && a.domain === b.domain,
        ),
        tap(({ uuid, domain }) => {
          this.uuid = uuid;
          this.domainName = domain;
          if (this.domainName) {
            this.landingService
              .getDomainForRedirection(this.domainName)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (response: any) => {
                  this.sourceName = response?.data?.source ?? null;
                  this.partnerInstituteDomainName =
                    response?.data?.domainname ?? null;
                  this.cdr.markForCheck();
                },
                error: (err: unknown) => {
                  console.error("Error fetching domain for redirection:", err);
                  this.cdr.markForCheck();
                },
              });
          }
          if (!uuid) {
            this.resetViewState();
            this.cdr.markForCheck();
          }
        }),
        filter(({ uuid }) => !!uuid),
        switchMap(({ uuid, domain }) => {
          const u = uuid as string;
          const job$ = this.landingService.getJobInviteDetails(u).pipe(
            catchError((err: unknown) => {
              console.error("Error fetching job details:", err);
              return of({ data: [] as ApiJob[] });
            }),
          );

          // SSR: only fetch job API. Browser without subdomain: also resolve stable-URL flag in parallel.
          if (!domain && this.isBrowser()) {
            const stable$ = this.landingService.stableJobURL().pipe(
              catchError((err: unknown) => {
                console.error("Error fetching stable job URLs:", err);
                return of([] as string[]);
              }),
            );
            return forkJoin({ jobResponse: job$, stableUrls: stable$ }).pipe(
              tap(({ stableUrls }) => {
                this.ifExist = this.computeStableUrlMatch(u, stableUrls);
              }),
              map(({ jobResponse }) => jobResponse),
            );
          }

          this.ifExist = false;
          return job$;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response: { data?: ApiJob[] }) => {
        this.setJobData(response.data?.[0] ?? null);
        this.cdr.markForCheck();
      });
  }

  private computeStableUrlMatch(
    currentUuid: string,
    response: unknown,
  ): boolean {
    if (!Array.isArray(response)) return false;
    for (const rawUrl of response) {
      if (typeof rawUrl !== "string") continue;
      const normalized = rawUrl.includes("\\/")
        ? rawUrl.replace(/\\\//g, "/")
        : rawUrl;
      if (normalized.split("/").pop() === currentUuid) {
        return true;
      }
    }
    return false;
  }

  private resetViewState(): void {
    this.data = null;
    this.schema = null;
    this.ifExist = false;
    this.isUniprep = false;
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private setJobData(data: ApiJob | null | undefined): void {
    if (!data) {
      return;
    }

    this.data = data;
    const company =
      data.company_name ?? (data as { comapany_name?: string }).comapany_name;
    this.isUniprep = company?.toLowerCase() === "uniprep";

    const pageUrl = this.getCurrentPageUrl();
    this.schema = buildJobPostingSchema(data, {
      siteName: "UNIPREP",
      baseJobUrl: "https://job.uniprep.ai/view",
      jobUrl: pageUrl || undefined,
      salaryUnit: "YEAR",
    }) as Record<string, unknown>;

    this.updateSeoTags({
      title: this.buildSeoTitle(data),
      description: this.buildMetaDescription(data),
      keywords: "UNIPREP, UNIPREP EMPLOYER",
      image: data.company_logo_url ? encodeURI(data.company_logo_url) : "",
      url: pageUrl,
    });
  }

  /**
   * Prefer injected REQUEST URL on the server (SSR); avoid `window` on the server.
   */
  private getCurrentPageUrl(): string {
    const reqUrl =
      this.request && "url" in this.request ? this.request.url : "";
    if (reqUrl) {
      return reqUrl;
    }
    if (this.isBrowser()) {
      return globalThis.location?.href ?? "";
    }
    return "";
  }

  /** `{Job Title} Jobs in {City} {Year} | UNIPREP` */
  private buildSeoTitle(job: ApiJob): string {
    const city =
      this.primaryLocationPhrase(job.work_location) ??
      this.stripLocationCountry(job.work_location) ??
      "India";
    const year = new Date().getFullYear();
    return `${job.position} Jobs in ${city} ${year} | UNIPREP`;
  }

  /**
   * `Apply for {Job Title} jobs in {City} at UNIPREP. {Work Type}, {Work Mode} with {SalaryRange} salary, and great benefits. Apply now.`
   */
  private buildMetaDescription(job: ApiJob): string {
    const city =
      this.primaryLocationPhrase(job.work_location) ??
      this.stripLocationCountry(job.work_location) ??
      "India";

    const workType = this.formatWorkTypeLabel(job);
    const workMode = this.formatWorkModeLabel(job);
    const parts = [workType, workMode].filter(Boolean) as string[];
    const detail = parts.length > 0 ? parts.join(", ") : "Flexible";

    const salaryRange = this.formatSalaryRange(job) ?? "competitive";

    const desc = `Apply for ${job.position} jobs in ${city} at UNIPREP. ${detail} with ${salaryRange} salary, and great benefits. Apply now.`;

    return desc.length > 300 ? desc.slice(0, 297).trimEnd() + "..." : desc;
  }

  private primaryLocationPhrase(workLocation?: string): string | undefined {
    if (!workLocation?.trim()) return undefined;
    const first = workLocation.split(",")[0]?.trim();
    return first || undefined;
  }

  private stripLocationCountry(workLocation?: string): string | undefined {
    if (!workLocation?.trim()) return undefined;
    const t = workLocation.trim();
    return t.length > 0 ? t : undefined;
  }

  /** Employment type for meta (e.g. Full-time). */
  private formatWorkTypeLabel(job: ApiJob): string | undefined {
    for (const raw of job.employment_type ?? []) {
      const e = raw.toLowerCase();
      if (e.includes("full")) return "Full-time";
      if (e.includes("part")) return "Part-time";
      if (e.includes("contract")) return "Contract";
    }
    return undefined;
  }

  /** Work mode for meta (e.g. Remote). */
  private formatWorkModeLabel(job: ApiJob): string | undefined {
    const wm = (job.work_mode ?? []).join(" ").toLowerCase();
    if (wm.includes("remote") || wm.includes("work from home")) return "Remote";
    if (wm.includes("hybrid")) return "Hybrid";
    if (wm.includes("on-site") || wm.includes("onsite")) return "On-site";
    return undefined;
  }

  /**
   * Monthly amount from API (no conversion). Used inside `with {SalaryRange} salary`.
   */
  private formatSalaryRange(job: ApiJob): string | null {
    if (job.salary_offer == null || !job.currency_code) return null;
    const code = job.currency_code.toUpperCase();
    const amount = Math.round(Number(job.salary_offer));
    if (code === "INR") {
      return `₹${amount.toLocaleString("en-IN")}/month`;
    }
    if (code === "USD") {
      return `$${amount.toLocaleString("en-US")}/month`;
    }
    return `${amount.toLocaleString("en-IN")} ${code}/month`;
  }

  getSalaryUpperBound(salary: number | null | undefined): number | null {
    if (salary == null || !Number.isFinite(Number(salary))) return null;
    return Math.round(Number(salary) * 1.3);
  }

  getProficiencyRating(proficiency: string): number {
    const proficiencyList: Record<string, number> = {
      Beginner: 2,
      Fluent: 3,
      Proficient: 4,
      Native: 5,
    };
    return proficiencyList[proficiency] ?? 0;
  }

  getRegisterLink(
    ifExist: boolean,
    data: { position: string; id: number | string },
  ): string {
    const position = encodeURIComponent(data.position);
    const jobId = encodeURIComponent(String(data.id));
    const queryParams = `position=${position}&job_id=${jobId}`;
    const isNumericDomain = !!this.domainName && /^\d+$/.test(this.domainName);

    if (String(this.sourceName ?? "").toLowerCase() === "freelancer") {
      const partnerId = encodeURIComponent(this.domainName as string);
      return `https://uniprep.ai/register?position=${position}&job_id=${jobId}&partner_id=${partnerId}`;
    }

    if (isNumericDomain) {
      const partnerId = encodeURIComponent(this.domainName as string);
      const host = this.safeRegisterHost();
      if (!host) {
        return `https://uniprep.ai/register?position=${position}&job_id=${jobId}&partner_id=${partnerId}`;
      }
      return `https://${host}/register?position=${position}&job_id=${jobId}&partner_id=${partnerId}`;
    }

    if (this.domainName) {
      return `https://${this.domainName}.uniprep.ai/register?position=${position}&job_id=${jobId}`;
    }
    return ifExist
      ? "https://employer.uniprep.ai/1/register"
      : `https://uniprep.ai/register?${queryParams}`;
  }

  private safeRegisterHost(): string | null {
    const raw = String(this.partnerInstituteDomainName ?? "").trim();
    if (!raw || raw === "undefined" || raw === "null") {
      return null;
    }

    try {
      const host = (
        raw.includes("://") ? new URL(raw).host : raw.replace(/^\/+/, "")
      )
        .split("/")[0]
        .toLowerCase();
      if (!host || host === "undefined" || host === "null" || !host.includes(".")) {
        return null;
      }
      return host;
    } catch {
      return null;
    }
  }

  updateSeoTags(config: {
    title: string;
    description?: string;
    keywords?: string;
    image?: string;
    url?: string;
  }): void {
    const {
      title,
      description = "",
      keywords = "",
      image = "",
      url = "",
    } = config;

    this.title.setTitle(title);
    this.meta.updateTag({ name: "description", content: description });
    this.meta.updateTag({ name: "keywords", content: keywords });
    this.meta.updateTag({ property: "og:title", content: title });
    this.meta.updateTag({ property: "og:description", content: description });
    this.meta.updateTag({ property: "og:image", content: image });
    this.meta.updateTag({ property: "og:url", content: url });
    this.meta.updateTag({ property: "og:type", content: "website" });
    this.meta.updateTag({ property: "og:logo", content: image });
    this.meta.updateTag({ name: "twitter:title", content: title });
    this.meta.updateTag({ name: "twitter:description", content: description });
    this.meta.updateTag({ name: "twitter:image", content: image });
    this.meta.updateTag({
      name: "twitter:card",
      content: "summary_large_image",
    });

    if (url) {
      this.setCanonicalLink(url);
    }
  }

  /** SSR-safe: update existing canonical or append once (no duplicate links). */
  private setCanonicalLink(url: string): void {
    const head = this.document.head;
    if (!head) return;

    let link = this.document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement("link");
      link.setAttribute("rel", "canonical");
      head.appendChild(link);
    }
    link.setAttribute("href", url);
  }

  applyNow(): void {
    if (this.isBrowser()) {
      globalThis.open?.(
        "https://shorturl.at/bFaAv",
        "_blank",
        "noopener,noreferrer",
      );
    }
  }
}
